import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './SharedDocumentDownloadPage.css';

// Public landing page for share links. The email button used to point directly
// at api-*.rialty.ai/api/v1/files/download/{token}; some browsers (esp. Chrome
// PDF viewer settings + cross-origin from Gmail's click-tracker redirects)
// would silently render the PDF inline or drop the download without touching
// the user's Downloads folder. Routing through the frontend gives us same-
// origin control: fetch → Blob → programmatic <a download> click, which every
// browser honors reliably.
export default function SharedDocumentDownloadPage() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | success | error
  const [fileName, setFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const attemptedRef = useRef(false);
  const objectUrlRef = useRef(null);

  const triggerDownload = async () => {
    setState('loading');
    setErrorMessage('');
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/files/download/${encodeURIComponent(token)}`);
      if (!resp.ok) {
        if (resp.status === 410) {
          setErrorMessage('This share link has expired or was revoked. Ask the sender for a new link.');
        } else if (resp.status === 404) {
          setErrorMessage('Share link not found. Double-check the URL from your email.');
        } else {
          setErrorMessage(`Download failed (status ${resp.status}). Please try again in a moment.`);
        }
        setState('error');
        return;
      }

      // Prefer server-supplied filename from Content-Disposition. Falls back to
      // a generic name — the browser will still save it, user can rename.
      const disposition = resp.headers.get('content-disposition') || '';
      let name = 'shared-document';
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const quotedMatch = disposition.match(/filename="([^"]+)"/i);
      const bareMatch = disposition.match(/filename=([^;]+)/i);
      if (utf8Match) {
        try { name = decodeURIComponent(utf8Match[1]); } catch (e) { name = utf8Match[1]; }
      } else if (quotedMatch) {
        name = quotedMatch[1];
      } else if (bareMatch) {
        name = bareMatch[1].trim();
      }
      setFileName(name);

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;

      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setState('success');
    } catch (e) {
      setErrorMessage('Network error — check your connection and try again.');
      setState('error');
    }
  };

  useEffect(() => {
    if (attemptedRef.current || !token) return;
    attemptedRef.current = true;
    triggerDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  return (
    <div className="share-dl-page">
      <div className="share-dl-card">
        <div className="share-dl-brand">Rialty</div>
        {state === 'loading' && (
          <>
            <div className="share-dl-spinner" aria-hidden="true"></div>
            <h1 className="share-dl-title">Preparing your download…</h1>
            <p className="share-dl-body">This shouldn't take more than a few seconds.</p>
          </>
        )}
        {state === 'success' && (
          <>
            <div className="share-dl-check" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1 className="share-dl-title">Downloaded</h1>
            <p className="share-dl-body">
              {fileName ? <><strong>{fileName}</strong> has been saved to your Downloads folder.</> : 'Your file has been saved to your Downloads folder.'}
            </p>
            <button className="share-dl-secondary-btn" onClick={triggerDownload}>Download again</button>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="share-dl-error" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h1 className="share-dl-title">Couldn't download</h1>
            <p className="share-dl-body">{errorMessage}</p>
            <button className="share-dl-primary-btn" onClick={triggerDownload}>Try again</button>
          </>
        )}
      </div>
    </div>
  );
}
